import { pdf, PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { ArrowLeft, ChevronDown, Download, FileText } from 'lucide-react';
import { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import AppHeader from '../components/AppHeader';
import InformeEvaluacionPDF from '../components/InformeEvaluacionPDF';
import AppPageLayout from '../layout/AppPageLayout';
import api from '../services/api';
import { descargarPdfNativo } from '../services/downloadService';
import { isNativeApp } from '../services/nativeBridge';
import { useEvaluationStore } from '../store/evaluationStore';

const PdfCanvasViewer = lazy(() => import('../components/PdfCanvasViewer'));

function slugifyFileName(value) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeRouteId(value) {
  if (!value) {
    return '';
  }

  if (value.startsWith('evaluacion-') || value.startsWith('toolbox-') || value.startsWith('sesion-')) {
    return value.split('-').pop() || '';
  }

  return value;
}

function formatPreviewDate(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

async function handleNativePdfDownload(pdfBlob, fileName, informeId) {
  if (!pdfBlob || !informeId) {
    return false;
  }

  try {
    const url = await api.uploadPdfInforme(informeId, pdfBlob, fileName);
    if (!url) {
      console.error('[download] El backend no devolvió URL de descarga');
      return false;
    }
    return descargarPdfNativo(url, fileName);
  } catch (err) {
    console.error('[download] Fallo la subida del PDF al backend', err);
    return false;
  }
}

function getPdfBlobErrorMessage(pdfBlobError) {
  if (!pdfBlobError) {
    return '';
  }

  if (typeof pdfBlobError === 'string') {
    return pdfBlobError;
  }

  return pdfBlobError.message || 'No se pudo preparar la vista previa del PDF.';
}

async function buildEvaluacionPreview(informeId, tipo) {
  // Si es amarre, buscar todos los informes del evento
  if (tipo === 'amarre') {
    const informesResp = await api.get(`/api/informes/?estado=4&evento=${informeId}`);
    const informes = informesResp.data || [];
    if (!informes.length) throw new Error('No se encontraron informes de amarre');

    const primerInforme = informes[0];
    const firmaDataUrl = await api.fetchArchivoComoDataUrl(primerInforme.firma);

    // Obtener preguntas del primer informe (todas tienen las mismas)
    const itemsResp = await api.get(`/api/informes/items?evaluacion=${primerInforme.id_evaluacion}`);
    const items = itemsResp.data || [];
    const item = items[0];
    if (!item) throw new Error('No se encontraron items');

    // Evidencia compartida del evento: se lee del ítem canónico (primer informe),
    // mismo patrón que trianual, cruzando por id_pregunta_item.
    const previewPorPregunta = {};
    const evidencias = await api.fetchEvidenciasInforme(item.id);
    await Promise.all(
      evidencias
        .filter((ev) => ev.id_pregunta_item != null && ev.referencia_archivo)
        .map(async (ev) => {
          const dataUrl = await api.fetchEvidenciaComoDataUrl(ev.referencia_archivo);
          if (dataUrl) {
            previewPorPregunta[ev.id_pregunta_item] = {
              dataUrl,
              nombre: ev.referencia_archivo,
            };
          }
        })
    );

    const preguntas = (item.Item_Evaluacion?.[0]?.Preguntas || []).map((p, idx) => {
      const evid = previewPorPregunta[p.id];
      return {
        id: String(idx + 1),
        preguntaId: p.id,
        pregunta: p.titulo,
        respuesta: (() => {
          const r = p.Respuesta?.[0]?.respuesta;
          if (r === 'cumple') return 'si';
          if (r === 'no_cumple') return 'no';
          if (r === 'no_aplica') return 'no aplica';
          return '';
        })(),
        observacion: '',
        requiere_evidencia: false,
        evidencia: evid ? 'Evidencia' : '',
        evidenciaPreview: evid ? evid.dataUrl : '',
      };
    });

    const operariosEvaluados = informes.map((inf, idx) => ({
      id: inf.id_evaluado,
      item: idx + 1,
      legajo: (() => {
        try {
          const m = typeof inf.Evaluado?.metadata === 'string'
            ? JSON.parse(inf.Evaluado.metadata)
            : inf.Evaluado?.metadata || {};
          return m.legajo || String(inf.id_evaluado);
        } catch { return String(inf.id_evaluado); }
      })(),
      nombreCompleto: (inf.evaluado_nombre || '').trim(),
    }));

    return {
      id: informeId,
      nombreEvaluado: operariosEvaluados.length > 1
        ? `${operariosEvaluados.length} OPERARIOS EVALUADOS`
        : operariosEvaluados[0]?.nombreCompleto || '-',
      legajoEvaluado: operariosEvaluados.map(o => o.legajo).join(', '),
      fecha: formatPreviewDate(primerInforme.fecha_programada || primerInforme.alta),
      fechaHora: primerInforme.fecha_programada || primerInforme.alta,
      tipoReporte: 'amarre',
      etiquetaReporte: 'AMARRE',
      nombreResponsable: (primerInforme.evaluador_nombre || '').trim() || '-',
      operariosEvaluados,
      firma: firmaDataUrl || '',
      preguntas,
    };
  }

  // Flujo trianual original...
  // GET informe con items y respuestas
  const informeResponse = await api.get(`/api/informes/${informeId}`);
  const informe = informeResponse.data?.[0];
  if (!informe) throw new Error('Informe no encontrado');

  const firmaDataUrl = await api.fetchArchivoComoDataUrl(informe.firma);

  const id_evaluacion = informe.id_evaluacion;

  // GET items con respuestas
  const itemsResponse = await api.get(
    `/api/informes/items?evaluacion=${id_evaluacion}`
  );
  const items = itemsResponse.data || [];
  const item = items[0];
  if (!item) throw new Error('Items del informe no encontrados');

  const previewPorPregunta = {};
  const evidencias = await api.fetchEvidenciasInforme(item.id);
  await Promise.all(
    evidencias
      .filter((ev) => ev.id_pregunta_item != null && ev.referencia_archivo)
      .map(async (ev) => {
        const dataUrl = await api.fetchEvidenciaComoDataUrl(ev.referencia_archivo);
        if (dataUrl) {
          previewPorPregunta[ev.id_pregunta_item] = {
            dataUrl,
            nombre: ev.referencia_archivo,
          };
        }
      })
  );

  const preguntas = (item.Item_Evaluacion?.[0]?.Preguntas || [])
    .filter((p) => typeof p === 'object')
    .map((p, idx) => {
      const evid = previewPorPregunta[p.id];
      return {
        id: String(idx + 1),
        preguntaId: p.id,
        pregunta: p.titulo,
        respuesta: (() => {
          const r = p.Respuesta?.[0]?.respuesta;
          if (r === 'cumple') return 'si';
          if (r === 'no_cumple') return 'no';
          if (r === 'no_aplica') return 'no aplica';
          return '';
        })(),
        observacion: '',
        requiere_evidencia: false,
        evidencia: evid ? 'Evidencia' : '',
        evidenciaPreview: evid ? evid.dataUrl : '',
      };
    });

  return {
    id: informe.id,
    nombreEvaluado: (informe.evaluado_nombre || informe.evento_nombre || '').trim(),
    legajoEvaluado: (() => {
      try {
        const evaluado = informe.Evaluado;
        if (!evaluado) return informe.id_evaluado ? String(informe.id_evaluado) : '-';
        // Intentar extraer legajo de metadata
        const metadata = typeof evaluado.metadata === 'string'
          ? JSON.parse(evaluado.metadata)
          : evaluado.metadata || {};
        return metadata.legajo || (informe.id_evaluado ? String(informe.id_evaluado) : '-');
      } catch {
        return informe.id_evaluado ? String(informe.id_evaluado) : '-';
      }
    })(),
    fecha: formatPreviewDate(informe.fecha_programada || informe.alta),
    fechaHora: informe.fecha_programada || informe.alta,
    tipoReporte: (() => {
      const nombre = (informe.evento_nombre || '').toLowerCase();
      const tituloItem = (informe.Items?.[0]?.titulo || '').toLowerCase();
      if (nombre.includes('amarre') || tituloItem.includes('amarre')) return 'amarre';
      return 'trianual';
    })(),
    etiquetaReporte: (() => {
      const nombre = (informe.evento_nombre || '').toLowerCase();
      const tituloItem = (informe.Items?.[0]?.titulo || '').toLowerCase();
      if (nombre.includes('amarre') || tituloItem.includes('amarre')) return 'AMARRE';
      return 'TRIANUAL';
    })(),
    nombreResponsable: informe.evaluador_nombre || '-',
    operariosEvaluados: [],
    firma: firmaDataUrl || '',
    preguntas
  };
}

async function buildToolboxPreview(id_evento) {
  const response = await api.get(`/api/informes/?estado=4&evento=${id_evento}`);
  const informes = response.data || [];
  if (!informes.length) throw new Error('No se encontraron informes del toolbox');

  // El listado viene en orden id DESC; el ítem canónico (donde vive la evidencia
  // compartida del evento) es el del PRIMER informe creado = menor id.
  const informesOrdenados = [...informes].sort((a, b) => a.id - b.id);
  const primerInforme = informesOrdenados[0];
  const firmaDataUrl = await api.fetchArchivoComoDataUrl(primerInforme.firma);

  // La evidencia MVP se sube solo al ítem canónico, pero se recorren los ítems de
  // todos los informes del evento (en orden) para ser robustos ante cualquier
  // orden o informe que la contenga. Se corta al encontrar la primera con foto.
  const evidenciasDescargadas = [];
  for (const informe of informesOrdenados) {
    if (!informe.id_evaluacion) continue;

    const itemsResp = await api.get(
      `/api/informes/items?evaluacion=${informe.id_evaluacion}`
    );
    const items = itemsResp.data || [];

    for (const item of items) {
      const evidencias = await api.fetchEvidenciasInforme(item.id);
      const conArchivo = evidencias.filter((ev) => ev.referencia_archivo);
      // eslint-disable-next-line no-await-in-loop
      const descargadas = await Promise.all(
        conArchivo.map((ev) => api.fetchEvidenciaComoDataUrl(ev.referencia_archivo))
      );
      descargadas
        .filter(Boolean)
        .forEach((dataUrl) => evidenciasDescargadas.push({ dataUrl }));
    }

    if (evidenciasDescargadas.length) break;
  }

  const preguntas = evidenciasDescargadas.map((ev, idx) => ({
    id: String(idx + 1),
    pregunta: 'Evidencia fotográfica',
    respuesta: '',
    evidencia: 'Evidencia',
    evidenciaPreview: ev.dataUrl,
  }));

  const operariosEvaluados = informes.map((informe) => {
    try {
      const evaluado = informe.Evaluado;
      const metadata = typeof evaluado?.metadata === 'string'
        ? JSON.parse(evaluado.metadata)
        : evaluado?.metadata || {};
      return {
        nombre: informe.evaluado_nombre || '',
        legajo: metadata.legajo || String(informe.id_evaluado || ''),
      };
    } catch {
      return {
        nombre: informe.evaluado_nombre || '',
        legajo: String(informe.id_evaluado || ''),
      };
    }
  });

  const nombreEvaluado = operariosEvaluados.length === 1
    ? operariosEvaluados[0].nombre
    : `${operariosEvaluados.length} participantes`;

  return {
    id: id_evento,
    nombreEvaluado,
    legajoEvaluado: `${operariosEvaluados.length} participante(s)`,
    fecha: formatPreviewDate(primerInforme.fecha_programada || primerInforme.alta),
    fechaHora: primerInforme.fecha_programada || primerInforme.alta,
    tipoReporte: 'toolbox',
    etiquetaReporte: 'TOOLBOX',
    nombreResponsable: primerInforme.evaluador_nombre || '-',
    operariosEvaluados,
    firma: firmaDataUrl || '',
    preguntas,
  };
}

function PreviewToolbar({ fileName, document, pdfBlob, pdfBlobLoading, informeId }) {
  const [downloading, setDownloading] = useState(false);
  const nativeApp = isNativeApp();
  const nativeDownloadPending = downloading || pdfBlobLoading || !pdfBlob;

  const onNativeDownload = async () => {
    setDownloading(true);
    try {
      const success = await handleNativePdfDownload(pdfBlob, fileName, informeId);
      console.log(
        success
          ? '[download] PDF guardado via bridge nativo'
          : '[download] Fallo la descarga nativa del PDF'
      );
    } catch (err) {
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="overflow-hidden rounded-t-[18px] bg-[#15a7c8] px-4 py-4 text-white sm:px-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-white/12">
            <FileText className="h-5 w-5" strokeWidth={2.3} />
          </div>

          <div>
            <p className="font-login text-[18px] font-semibold leading-none">
              Guardar como PDF
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 font-login text-[13px]">
              <span>
                Copias: <strong className="font-bold">01</strong>
              </span>
              <span className="inline-flex items-center gap-1">
                Tamano del papel: <strong className="font-bold">ISO A4</strong>
                <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.4} />
              </span>
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-3">
          <ChevronDown className="mt-1 h-5 w-5" strokeWidth={2.4} />

          {nativeApp ? (
            <button
              type="button"
              onClick={onNativeDownload}
              disabled={nativeDownloadPending}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#ffbe16] text-[#ffffff] shadow-[0_10px_24px_rgba(255,190,22,0.45)] disabled:opacity-60"
            >
              <Download className="h-6 w-6" strokeWidth={2.5} />
              <span className="sr-only">
                {downloading
                  ? 'Descargando...'
                  : pdfBlobLoading
                    ? 'Preparando PDF...'
                    : 'Descargar PDF'}
              </span>
            </button>
          ) : (
            <PDFDownloadLink document={document} fileName={fileName}>
              {({ loading }) => (
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#ffbe16] text-[#ffffff] shadow-[0_10px_24px_rgba(255,190,22,0.45)]">
                  <Download className="h-6 w-6" strokeWidth={2.5} />
                  <span className="sr-only">
                    {loading ? 'Preparando PDF...' : 'Descargar PDF'}
                  </span>
                </span>
              )}
            </PDFDownloadLink>
          )}
        </div>
      </div>
    </div>
  );
}

function MobileDownloadPanel({
  fileName,
  document,
  evaluacion,
  pdfBlob,
  pdfBlobLoading,
  pdfBlobError,
  informeId,
}) {
  const [downloading, setDownloading] = useState(false);
  const nativeApp = isNativeApp();
  const nativeDownloadPending = downloading || pdfBlobLoading || !pdfBlob;

  const onNativeDownload = async () => {
    setDownloading(true);
    try {
      const success = await handleNativePdfDownload(pdfBlob, fileName, informeId);
    } catch (err) {
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="rounded-b-[18px] bg-white px-4 pb-5 pt-5">
      {nativeApp ? (
        <div className="mb-4">
          {pdfBlobLoading ? (
            <p className="font-login text-[14px] text-[#5f6673]">
              Preparando vista previa...
            </p>
          ) : null}
          {pdfBlobError ? (
            <p className="font-login text-[14px] text-[#b42318]" role="alert">
              {getPdfBlobErrorMessage(pdfBlobError)}
            </p>
          ) : null}
          {pdfBlob ? (
            <Suspense
              fallback={
                <p className="font-login text-[14px] text-[#5f6673]">
                  Cargando visor...
                </p>
              }
            >
              <PdfCanvasViewer
                blob={pdfBlob}
                className="h-[60vh] overflow-y-auto rounded-[12px] border border-[#cfd7e1]"
              />
            </Suspense>
          ) : null}
        </div>
      ) : null}

      <div className="rounded-[14px] border border-[#d7dce2] bg-[#fafbfc] p-4 shadow-[0_6px_20px_rgba(15,23,42,0.08)]">
        <p className="font-login text-[16px] font-bold text-[#222222]">
          Informe listo para descargar
        </p>
        <p className="mt-2 font-login text-[14px] text-[#7f8b9d]">
          Evaluado: {evaluacion.nombreEvaluado}
        </p>
        <p className="mt-1 font-login text-[14px] text-[#7f8b9d]">
          Fecha: {evaluacion.fecha}
        </p>

        <div className="mt-5">
          {nativeApp ? (
            <button
              type="button"
              onClick={onNativeDownload}
              disabled={nativeDownloadPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#2e86b5] px-4 py-3 font-login text-[15px] font-bold text-white disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {downloading
                ? 'Descargando...'
                : pdfBlobLoading
                  ? 'Preparando PDF...'
                  : 'Descargar PDF'}
            </button>
          ) : (
            <PDFDownloadLink document={document} fileName={fileName}>
              {({ loading }) => (
                <span className="inline-flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#2e86b5] px-4 py-3 font-login text-[15px] font-bold text-white">
                  <Download className="h-4 w-4" />
                  {loading ? 'Preparando PDF...' : 'Descargar PDF'}
                </span>
              )}
            </PDFDownloadLink>
          )}
        </div>
      </div>
    </div>
  );
}

function InformePreviewPage() {
  const navigate = useNavigate();
  const { tipo, informeId, evaluacionId } = useParams();
  const evaluacionesCompletadas = useEvaluationStore(
    (state) => state.evaluacionesCompletadas
  );
  const resolvedRouteId = normalizeRouteId(informeId || evaluacionId);
  const storedEvaluacion = evaluacionesCompletadas.find(
    (item) => String(item.id) === String(resolvedRouteId)
  );
  const resolvedType =
    tipo ||
    (storedEvaluacion?.tipoReporte ? storedEvaluacion.tipoReporte.toLowerCase() : '') ||
    'trianual';
  const [evaluacion, setEvaluacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadPreview() {
      if (!resolvedRouteId) {
        setLoading(false);
        return;
      }

      // Siempre cargar desde backend para trianuales

      setLoading(true);
      setError(null);

      try {
        const nextEvaluacion =
          resolvedType === 'toolbox'
            ? await buildToolboxPreview(resolvedRouteId)
            : await buildEvaluacionPreview(resolvedRouteId, resolvedType);

        if (!ignore) {
          setEvaluacion(nextEvaluacion);
        }
      } catch (nextError) {
        if (!ignore) {
          setError(nextError.message || 'No se pudo cargar el informe.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      ignore = true;
    };
  }, [resolvedRouteId, resolvedType, storedEvaluacion]);

  const document = useMemo(() => {
    if (!evaluacion) {
      return null;
    }

    return <InformeEvaluacionPDF evaluacion={evaluacion} />;
  }, [evaluacion]);

  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfBlobLoading, setPdfBlobLoading] = useState(false);
  const [pdfBlobError, setPdfBlobError] = useState(null);

  useEffect(() => {
    if (!document || !isNativeApp()) {
      setPdfBlob(null);
      setPdfBlobLoading(false);
      setPdfBlobError(null);
      return undefined;
    }

    let cancelled = false;

    async function generatePdfBlob() {
      setPdfBlobLoading(true);
      setPdfBlobError(null);
      setPdfBlob(null);

      try {
        const blob = await pdf(document).toBlob();
        if (!cancelled) {
          setPdfBlob(blob);
        }
      } catch (nextError) {
        if (!cancelled) {
          setPdfBlobError(nextError);
        }
      } finally {
        if (!cancelled) {
          setPdfBlobLoading(false);
        }
      }
    }

    generatePdfBlob();

    return () => {
      cancelled = true;
    };
  }, [document]);

  if (!resolvedRouteId) {
    return <Navigate to="/reportes" replace />;
  }

  if (loading) {
    return (
      <AppPageLayout>
        <AppHeader />
        <div className="mx-auto mt-10 w-full max-w-[760px] font-login text-[16px] text-[#5f6673]">
          Cargando vista previa...
        </div>
      </AppPageLayout>
    );
  }

  if (error || !evaluacion || !document) {
    return <Navigate to="/reportes" replace />;
  }

  const fileName = `informe-${slugifyFileName(
    evaluacion.nombreEvaluado
  )}-${slugifyFileName(evaluacion.fecha)}.pdf`;
  const nativeApp = isNativeApp();

  return (
    <AppPageLayout>
      <AppHeader
        rightContent={
          <button
            type="button"
            onClick={() => navigate('/reportes')}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full text-[#111111] transition hover:bg-[#f5f7fa]"
            aria-label="Volver"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.8} />
          </button>
        }
      />

      <div className="mx-auto mt-6 w-full max-w-[1120px]">
        <div className="overflow-hidden rounded-[18px] border border-[#d7dce2] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]">
          <PreviewToolbar
            fileName={fileName}
            document={document}
            pdfBlob={pdfBlob}
            pdfBlobLoading={pdfBlobLoading}
            informeId={resolvedRouteId}
          />

          {nativeApp || isMobile ? (
            <MobileDownloadPanel
              fileName={fileName}
              document={document}
              evaluacion={evaluacion}
              pdfBlob={pdfBlob}
              pdfBlobLoading={pdfBlobLoading}
              pdfBlobError={pdfBlobError}
              informeId={resolvedRouteId}
            />
          ) : (
            <div className="bg-[#eef2f5] p-4 sm:p-5">
              <div className="h-[calc(100vh-250px)] min-h-[640px] overflow-hidden rounded-[12px] border border-[#cfd7e1] bg-white shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                <PDFViewer width="100%" height="100%" className="h-full w-full">
                  {document}
                </PDFViewer>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppPageLayout>
  );
}

export default InformePreviewPage;
